import React, { useState, useRef, useLayoutEffect } from "react";
import { any, string, oneOf, bool } from "prop-types";
import classnames from "classnames";
import { getBreakpoint } from "../utils";
import outerHeight from "../utils/dom/outer-height";
import { useScrollPosition } from "../utils/dom/useScrollPosition";
import { FixedPanelFixTo, FixedPanelBreakpoint } from "./fixed-panel.types";

export const FixedPanel = ({
  children,
  className,
  condenseOnScroll,
  dockingBreakPoint = null,
  fixTo,
  headerEl = null,
  hideAtEl = null,
  hideOnScroll,
  id,
}) => {
  const [panelDisplay, setPanelDisplay] = useState({
    condense: null,
    dock: null,
    fix: null,
    hideTop: null,
  });

  const scrollPosition = {
    current: 0,
    previous: 0,
  };

  const hideAtElRef = useRef(null);

  const panelInfo = {
    height: 0,
    ref: useRef(),
    hideAtEl: hideAtEl || hideAtElRef,
  };

  useLayoutEffect(() => {
    _validateHeader(headerEl);
    panelInfo.height = panelInfo.ref.current.getBoundingClientRect().height;
    if (fixTo === "top" && headerEl === null) handleNoHeaderPanel();
    if (typeof window === "undefined") return;
    window.addEventListener("resize", _onResize);

    return () => window.removeEventListener("resize", _onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelDisplay.dock, panelDisplay.fix]);

  useScrollPosition(
    ({ prevPos, currPos }) => {
      scrollPosition.current = currPos.window.y;
      scrollPosition.previous = prevPos.window.y;
      const headerHeight = _getHeaderHeight();

      if (condenseOnScroll) {
        scrollPosition.current >= 1 ? condensePanel() : extendPanel();
      }
      if (
        headerEl &&
        headerEl.current &&
        panelInfo.ref.current.parentNode !== headerEl.current
      ) {
        if (hideAtEl) {
          // currPos.element returns position of element to hide panel
          const currentElementPosition =
            currPos.element.y + scrollPosition.current;

          _fixToTopViewport(
            headerHeight,
            scrollPosition.current,
            currentElementPosition
          );
        } else {
          _fixToTopViewport(headerHeight, scrollPosition.current);
        }
      }
    },
    [
      condenseOnScroll,
      panelDisplay.condense,
      panelDisplay.fix,
      panelDisplay.dock,
    ],
    hideAtEl,
    50
  );

  const stylePageContent = (val) => {
    const element = styleElement(panelInfo.ref.current.nextSibling);
    element.set("margin-top", val);
  };

  const handleNoHeaderPanel = () => {
    // fix panel initially to prevent flicker
    const { condense, hideTop } = panelDisplay;

    if (shouldPanelDock()) {
      setPanelDisplay((prevState) => ({
        ...prevState,
        condense: condense,
        dock: shouldPanelDock(),
        fix: `is-fixed-${fixTo}`,
        hideTop: hideTop,
      }));
      stylePageContent(panelInfo.height);
    } else {
      setPanelDisplay((prevState) => ({
        ...prevState,
        condense: null,
        dock: shouldPanelDock(),
        fix: null,
        hideTop: null,
      }));
      stylePageContent(0);
    }
  };

  const styleElement = (element) => {
    const set = (prop, val) => {
      if (!element) return;
      element.style[prop] = val + "px";
      return element;
    };

    return { set, element };
  };

  const _isHeaderFixed = (headerEl) => {
    if (typeof window === "undefined") return;
    return (
      window
        .getComputedStyle(headerEl.current, null)
        .getPropertyValue("position") === "fixed"
    );
  };

  const isElementAdjacentToHeader = () => {
    if (headerEl.current.nextSibling !== panelInfo.ref.current) {
      throw new Error(
        "<FixedPanel> Panel must be the next sibling to the header"
      );
    }
  };

  const isElementChildOfHeader = () => {
    if (!headerEl.current.querySelector(".spark-panel--fixed")) {
      throw new Error("<FixedPanel> Panel must be a child of a fixed header");
    }
  };

  /**
   * Check if panel is child of header or adjacent to header.
   * Styling is added to the next adjacent element when
   * fixed panel is taken out of the flow of content.
   *
   * @param {Element} headerEl
   */

  const _validateHeader = (headerEl) => {
    if (!headerEl) return styleElement(panelInfo.ref.current.nextSibling);
    if (headerEl && !headerEl.current) return;
    if (_isHeaderFixed(headerEl)) {
      isElementChildOfHeader();
    } else {
      isElementAdjacentToHeader();
      styleElement(panelInfo.ref.current.nextSibling);
    }
  };

  /**
   * This method checks the current viewport size against the breakpoint
   * set in the configs, returning false if the viewport size is less
   * than or equal to the breakpoint config and true if it is not.
   */
  const shouldPanelDock = () => {
    if (typeof window === "undefined") return;
    const windowWidth = window.innerWidth;
    const currentBreakpoint = getBreakpoint(windowWidth);

    if (dockingBreakPoint === currentBreakpoint) return false;
    if (dockingBreakPoint === "sm" && currentBreakpoint === "xs") return false;
    if (
      dockingBreakPoint === "md" &&
      (currentBreakpoint === "sm" || currentBreakpoint === "xs")
    )
      return false;
    if (
      dockingBreakPoint === "lg" &&
      (currentBreakpoint === "md" ||
        currentBreakpoint === "sm" ||
        currentBreakpoint === "xs")
    )
      return false;
    if (dockingBreakPoint === "xl") return false;

    return true;
  };

  const _getHeaderHeight = () => {
    if (!headerEl) return false;
    const headerTop =
      headerEl.current.getBoundingClientRect().top + scrollPosition.current;

    return headerTop + outerHeight(headerEl.current);
  };

  const _onResize = () => {
    const headerHeight = _getHeaderHeight();

    if (
      headerEl &&
      headerEl.current &&
      panelInfo.ref.current.parentNode !== headerEl.current
    )
      _fixToTopViewport(headerHeight, scrollPosition.current);
    if (fixTo === "top" && headerEl === null) handleNoHeaderPanel();
    _getHeaderHeight();
    panelInfo.height = panelInfo.ref.current.getBoundingClientRect().height;
  };

  const condensePanel = () => {
    const { dock, fix, hideTop } = panelDisplay;

    // do not condense if panel is undocked
    if (!shouldPanelDock()) return;

    setPanelDisplay((prevState) => ({
      ...prevState,
      condense: true,
      dock: dock,
      fix: fix,
      hideTop: hideTop,
    }));
  };

  const extendPanel = () => {
    const { dock, fix, hideTop } = panelDisplay;
    setPanelDisplay((prevState) => ({
      ...prevState,
      condense: false,
      dock: dock,
      fix: fix,
      hideTop: hideTop,
    }));
  };

  const handleHideOnScroll = (headerHeight) => {
    // keep panel viewing brief by hiding it after short scroll
    if (!hideOnScroll) return;

    // show panel when it scrolls into view
    return scrollPosition.current < headerHeight + panelInfo.height
      ? false
      : // eslint-disable-next-line no-inline-comments
      // hide panel after it has been scrolled past
      scrollPosition.current >= scrollPosition.previous
      ? true
      : false;
  };

  const _unFixPanel = (headerHeight, isDocked, isHidden) => {
    const { condense, fix } = panelDisplay;

    if (headerHeight === 0) {
      setPanelDisplay((prevState) => ({
        ...prevState,
        condense: condense,
        dock: isDocked,
        fix: fix,
        hideTop: isHidden,
      }));
      return;
    }
    setPanelDisplay((prevState) => ({
      ...prevState,
      condense: condense,
      dock: isDocked,
      fix: false,
      hideTop: isHidden,
    }));
    stylePageContent(0);
  };

  const _fixPanelToTop = (isDocked, isHidden) => {
    const { condense } = panelDisplay;

    if (!isDocked) {
      setPanelDisplay((prevState) => ({
        ...prevState,
        condense: condense,
        dock: isDocked,
        fix: false,
        hideTop: isHidden,
      }));
      stylePageContent(0);
    } else {
      setPanelDisplay((prevState) => ({
        ...prevState,
        condense: condense,
        dock: isDocked,
        fix: true,
        hideTop: isHidden,
      }));
      stylePageContent(panelInfo.height);
    }
  };

  const _fixToTopViewport = (headerHeight, currentScroll, elementPosition) => {
    const setHideTop = elementPosition
      ? _showUntilElement(currentScroll, elementPosition)
      : handleHideOnScroll(headerHeight);
    currentScroll > headerHeight
      ? _fixPanelToTop(shouldPanelDock(), setHideTop)
      : _unFixPanel(headerHeight, shouldPanelDock(), setHideTop);
  };

  const _showUntilElement = (windowPosition, elementPosition) => {
    if (typeof window === "undefined") return;
    const panelOuterHeight = outerHeight(panelInfo.ref.current);
    const headerOuterHeight = outerHeight(headerEl.current);
    const targetScrollPosition = _isHeaderFixed(headerEl)
      ? elementPosition - headerOuterHeight
      : elementPosition - panelOuterHeight;

    return windowPosition >= targetScrollPosition && windowPosition > 0
      ? true
      : false;
  };

  const { condense, fix, hideTop } = panelDisplay;

  return (
    <div
      ref={panelInfo.ref}
      id={id}
      className={classnames(
        "spark-panel--fixed",
        className,
        { "spark-panel--condensed": condense },
        { [`is-fixed-${fixTo}`]: fix },
        { "hide-panel-top": hideTop }
      )}
    >
      {children}
    </div>
  );
};

// Backwards compatibility to support component property. Enum import should be used instead.
FixedPanel.FIXED = FixedPanelFixTo;
FixedPanel.DOCK = FixedPanelBreakpoint;

FixedPanel.propTypes = {
  /** Main content to display inside the Panel */
  children: any,

  /** Any extra CSS classes for the component */
  className: string,

  /** Applies the `spark-panel--condensed` class once the scroll position exceeds the height of the Header and Panel. This class can then be used to add additional condensed styling to the contents of the Fixed Panel e.g. reducing font-sizes, margins or padding. */
  condenseOnScroll: bool,

  /** Sets the breakpoint at which the Fixed Panel behavior starts at. Options are `SMALL, MEDIUM, LARGE, XLARGE`. If not set the panel will dock at all breakpoints. */
  dockingBreakPoint: oneOf(Object.values(FixedPanelBreakpoint)),

  /** Position to affix panel to viewport. Currently, only `TOP` is available. If the panel is a child of the header this does not need to be set */
  fixTo: oneOf(Object.values(FixedPanelFixTo)),

  /** An element ref used if it is not a Spark Header. If using a Spark Header, this does not need to be supplied. */
  headerEl: function (props) {
    if (props.headerEl && props.headerEl.current === undefined) {
      return new Error("The prop `headerEl` must be a ref to a node element");
    }
  },

  /** An element ref used to determine the scroll position when the Fixed Panel should hide. If not set the panel will show at all times. */
  hideAtEl: function (props) {
    if (props.hideAtEl && props.hideAtEl.current === undefined) {
      return new Error("The prop `hideAtEl` must be a ref to a node element");
    }
  },

  /** Hides the Fixed Panel when a user scrolls. */
  hideOnScroll: bool,

  /** id for Fixed Panel */
  id: string,
};

FixedPanel.displayName = "FixedPanel";
